import React, { useState, useEffect, useRef } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { CheckinParticipant, PlayCheckinSound, formatMexicoDate } from './../../../../utilities/BL/MvpXolosZone';

export function MvpXolosZoneCheckinModal({ isOpen, onClose, onCheckinSuccess, totalWinners = 50, initialAttended = 0 }) {
    const { showMessage } = useFloatMessage();

    // Scan & Process States
    const [manualInput, setManualInput] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [attendedCount, setAttendedCount] = useState(initialAttended);

    // Mute State con persistencia y referencia mutable contra Stale Closure
    const [isMuted, setIsMuted] = useState(() => {
        return localStorage.getItem('mvpxoloszone_checkin_muted') === 'true';
    });
    const isMutedRef = useRef(isMuted);

    // Latest Result State
    const [scanResult, setScanResult] = useState(null); // { status, message, name, email, phone, passCode, winnerRank, attendedAtUtc, disqualifiedReason }

    // Keyboard Wedge Scanner Buffers
    const scanBufferRef = useRef('');
    const lastKeyTimeRef = useRef(0);
    const isManualFocusedRef = useRef(false);

    useEffect(() => {
        setAttendedCount(initialAttended);
    }, [initialAttended]);

    const handleToggleMute = () => {
        setIsMuted(prev => {
            const next = !prev;
            isMutedRef.current = next;
            localStorage.setItem('mvpxoloszone_checkin_muted', next ? 'true' : 'false');
            return next;
        });
    };

    // Handle Hardware Barcode / QR Scanner (Keyboard Wedge)
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            if (isManualFocusedRef.current) return;

            const now = Date.now();
            const timeDiff = now - lastKeyTimeRef.current;
            lastKeyTimeRef.current = now;

            if (e.key === 'Enter') {
                if (scanBufferRef.current.length >= 3) {
                    const scanned = scanBufferRef.current;
                    scanBufferRef.current = '';
                    processCheckin(scanned);
                } else {
                    scanBufferRef.current = '';
                }
                return;
            }

            // Normal typing delay vs fast hardware scanner (< 60ms)
            if (timeDiff > 120 && scanBufferRef.current.length > 0) {
                scanBufferRef.current = '';
            }

            if (e.key.length === 1) {
                scanBufferRef.current += e.key;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    const processCheckin = async (scanData) => {
        if (!scanData || isProcessing) return;
        setIsProcessing(true);

        try {
            const res = await CheckinParticipant(scanData);

            if (res.success && res.status === 'ACCESS_GRANTED') {
                if (!isMutedRef.current) PlayCheckinSound('success');
                setAttendedCount(res.totalAttended || (attendedCount + 1));
                setScanResult({
                    status: 'ACCESS_GRANTED',
                    message: res.message || '¡Acceso Concedido! Pase verificado para ingresar a cancha.',
                    name: res.name,
                    email: res.email,
                    phone: res.phone,
                    passCode: res.passCode,
                    winnerRank: res.winnerRank,
                    attendedAtUtc: res.attendedAtUtc || new Date().toISOString()
                });

                if (onCheckinSuccess) onCheckinSuccess(res);
            } else if (res.status === 'ALREADY_ATTENDED') {
                if (!isMutedRef.current) PlayCheckinSound('warning');
                setScanResult({
                    status: 'ALREADY_ATTENDED',
                    message: res.message || '⚠️ Pase ya utilizado previamente.',
                    name: res.name,
                    email: res.email,
                    phone: res.phone,
                    passCode: res.passCode,
                    winnerRank: res.winnerRank,
                    attendedAtUtc: res.attendedAtUtc
                });
            } else if (res.status === 'DISQUALIFIED') {
                if (!isMutedRef.current) PlayCheckinSound('error');
                setScanResult({
                    status: 'DISQUALIFIED',
                    message: res.message || '❌ Acceso Denegado. Participante descalificado.',
                    name: res.name,
                    email: res.email,
                    phone: res.phone,
                    passCode: res.passCode,
                    winnerRank: res.winnerRank,
                    disqualifiedReason: res.disqualifiedReason
                });
            } else {
                if (!isMutedRef.current) PlayCheckinSound('error');
                setScanResult({
                    status: 'INVALID',
                    message: res.message || 'No se encontró un pase de acceso válido.',
                    name: res.name || null,
                    passCode: res.passCode || null
                });
            }
        } catch (error) {
            console.error("Error en proceso de checkin:", error);
            if (!isMutedRef.current) PlayCheckinSound('error');
            setScanResult({
                status: 'ERROR',
                message: 'Error de conexión con el servidor al validar el pase.'
            });
        } finally {
            setIsProcessing(false);
            setManualInput('');
        }
    };

    const handleManualSubmit = (e) => {
        e.preventDefault();
        if (manualInput.trim()) {
            processCheckin(manualInput.trim());
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="modal fade show d-block" 
            tabIndex="-1" 
            style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(6px)', zIndex: 1080 }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content shadow-lg border-0 rounded-4 bg-white text-dark">
                    {/* Header */}
                    <div className="modal-header border-bottom px-4 py-3 bg-white">
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            <h5 className="modal-title fw-bold mb-0 d-flex align-items-center gap-2 text-danger">
                                <i className="bi bi-qr-code-scan"></i>
                                Control de Acceso a Cancha
                            </h5>
                            <span className="badge bg-danger px-3 py-2 fs-6 fw-bold">
                                MVP Xolos Femenil
                            </span>
                            <span className="badge bg-success-subtle text-success border border-success px-3 py-2 fs-6 fw-bold">
                                Ingresados: {attendedCount} / {totalWinners}
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-2 ms-auto">
                            <button 
                                type="button" 
                                className={`btn btn-sm ${isMuted ? 'btn-outline-danger' : 'btn-outline-secondary'} d-flex align-items-center gap-1.5 px-3 py-1.5 fw-semibold`}
                                onClick={handleToggleMute}
                                title={isMuted ? "Activar sonido" : "Silenciar sonido"}
                            >
                                <i className={`bi ${isMuted ? 'bi-volume-mute-fill text-danger fs-6' : 'bi-volume-up-fill text-success fs-6'}`}></i>
                                <span className="small">{isMuted ? 'Silenciado' : 'Sonido activo'}</span>
                            </button>
                            <button 
                                type="button" 
                                className="btn-close" 
                                aria-label="Close"
                                onClick={onClose}
                            ></button>
                        </div>
                    </div>

                    <div className="modal-body p-4 bg-light">
                        <div className="row g-4">
                            {/* Columna Izquierda: Escaneo y Controles */}
                            <div className="col-12 col-lg-5">
                                {/* Entrada Manual / Búsqueda */}
                                <div className="card border-0 bg-white p-4 rounded-3 shadow-sm h-100 d-flex flex-column justify-content-between">
                                    <div>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <i className="bi bi-upc-scan text-danger fs-5"></i>
                                            <h6 className="fw-bold text-dark mb-0">Escáner USB / Bluetooth o Folio Manual</h6>
                                        </div>
                                        <p className="text-secondary small mb-3">
                                            Apunta la pistola lectora al código QR o introduce manualmente el folio, correo o teléfono del aficionado:
                                        </p>

                                        <form onSubmit={handleManualSubmit}>
                                            <div className="input-group mb-2">
                                                <input 
                                                    type="text" 
                                                    className="form-control bg-light text-dark border-secondary-subtle font-monospace fs-5 py-2"
                                                    placeholder="#XOLOTIJ01..."
                                                    value={manualInput}
                                                    onChange={(e) => setManualInput(e.target.value)}
                                                    onFocus={() => { isManualFocusedRef.current = true; }}
                                                    onBlur={() => { isManualFocusedRef.current = false; }}
                                                    disabled={isProcessing}
                                                    autoFocus
                                                />
                                                <button 
                                                    type="submit" 
                                                    className="btn btn-danger fw-bold px-3 d-flex align-items-center gap-1"
                                                    disabled={isProcessing || !manualInput.trim()}
                                                >
                                                    {isProcessing ? (
                                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-arrow-return-left"></i>
                                                            <span>Validar</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </form>

                                        {isProcessing && (
                                            <div className="alert alert-warning py-2.5 px-3 rounded-3 small fw-bold d-flex align-items-center gap-2 mb-2 animate__animated animate__fadeIn">
                                                <div className="spinner-border spinner-border-sm text-warning" role="status"></div>
                                                <span>⏳ Validando código escaneado... Por favor espera.</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="bg-light p-3 rounded-3 border border-secondary-subtle mt-3">
                                        <div className="d-flex align-items-start gap-2">
                                            <i className="bi bi-info-circle-fill text-danger mt-0.5"></i>
                                            <div className="small text-secondary" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                                                <strong>Modo Continuo Activo:</strong> La pistola lectora USB / Bluetooth captura lecturas instantáneamente en cualquier momento sin necesidad de hacer clic en el campo de texto.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Columna Derecha: Tarjeta de Feedback Visual Grande */}
                            <div className="col-12 col-lg-7">
                                {isProcessing ? (
                                    <div className="card border-0 bg-white text-center p-5 rounded-4 shadow-sm h-100 d-flex justify-content-center align-items-center">
                                        <div className="spinner-grow text-danger mb-3" style={{ width: '4rem', height: '4rem' }} role="status"></div>
                                        <h4 className="fw-bold text-danger">Validando código escaneado...</h4>
                                        <p className="text-secondary small mb-0">Consultando base de datos segura y verificando firma HMAC...</p>
                                    </div>
                                ) : scanResult?.status === 'ACCESS_GRANTED' ? (
                                    <div className="card border-2 border-success bg-white text-center p-4 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center animate__animated animate__zoomIn">
                                        <div className="bg-success text-white rounded-circle p-3 mb-3 d-inline-flex justify-content-center align-items-center shadow-sm" style={{ width: '90px', height: '90px' }}>
                                            <i className="bi bi-check-lg" style={{ fontSize: '3.5rem' }}></i>
                                        </div>
                                        <span className="badge bg-success text-white fs-5 px-4 py-2 rounded-pill fw-bold mb-2">
                                            ✅ ACCESO CONCEDIDO
                                        </span>
                                        <h2 className="fw-bold text-dark text-uppercase mb-1 mt-2">
                                            {scanResult.name}
                                        </h2>
                                        <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap mb-3">
                                            <span className="badge bg-warning text-dark fs-5 font-monospace px-3 py-1 fw-bold">
                                                {scanResult.passCode}
                                            </span>
                                            <span className="badge bg-dark text-warning border border-warning fs-6 px-3 py-1 fw-bold">
                                                🏆 GANADOR #{scanResult.winnerRank}
                                            </span>
                                        </div>
                                        <p className="text-success fw-bold mb-2 fs-6">
                                            Pase individual auténtico verificado. Autorizado a pasar a cancha.
                                        </p>
                                        <div className="text-muted small">
                                            <i className="bi bi-clock me-1"></i> Registrado: {formatMexicoDate(scanResult.attendedAtUtc)}
                                        </div>
                                    </div>
                                ) : scanResult?.status === 'ALREADY_ATTENDED' ? (
                                    <div className="card border-2 border-warning bg-white text-center p-4 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center animate__animated animate__shakeX">
                                        <div className="bg-warning text-dark rounded-circle p-3 mb-3 d-inline-flex justify-content-center align-items-center shadow-sm" style={{ width: '90px', height: '90px' }}>
                                            <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '3.5rem' }}></i>
                                        </div>
                                        <span className="badge bg-warning text-dark fs-5 px-4 py-2 rounded-pill fw-bold mb-2">
                                            ⚠️ PASE YA UTILIZADO
                                        </span>
                                        <h3 className="fw-bold text-dark text-uppercase mb-1 mt-2">
                                            {scanResult.name}
                                        </h3>
                                        <span className="badge bg-dark text-warning fs-5 font-monospace px-3 py-1 fw-bold mb-3">
                                            {scanResult.passCode}
                                        </span>
                                        <div className="alert alert-warning border border-warning text-dark fw-bold px-4 py-2 rounded-3 mb-2">
                                            Este pase ya ingresó previamente a las: {formatMexicoDate(scanResult.attendedAtUtc)}
                                        </div>
                                        <p className="text-secondary small mb-0">
                                            No se permite el acceso repetido con el mismo código QR o captura de pantalla.
                                        </p>
                                    </div>
                                ) : scanResult?.status === 'DISQUALIFIED' ? (
                                    <div className="card border-2 border-danger bg-white text-center p-4 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center animate__animated animate__headShake">
                                        <div className="bg-danger text-white rounded-circle p-3 mb-3 d-inline-flex justify-content-center align-items-center shadow-sm" style={{ width: '90px', height: '90px' }}>
                                            <i className="bi bi-shield-x" style={{ fontSize: '3.5rem' }}></i>
                                        </div>
                                        <span className="badge bg-danger text-white fs-5 px-4 py-2 rounded-pill fw-bold mb-2">
                                            ❌ ACCESO DENEGADO
                                        </span>
                                        <h3 className="fw-bold text-dark text-uppercase mb-1 mt-2">
                                            {scanResult.name}
                                        </h3>
                                        <span className="badge bg-dark text-danger fs-5 font-monospace px-3 py-1 fw-bold mb-3">
                                            {scanResult.passCode}
                                        </span>
                                        <div className="alert alert-danger border-0 bg-danger text-white fw-bold px-4 py-2 rounded-3 mb-2">
                                            Participante descalificado: {scanResult.disqualifiedReason || 'Sin motivo registrado'}
                                        </div>
                                        <p className="text-secondary small mb-0">
                                            Pase cancelado en el sistema por el administrador.
                                        </p>
                                    </div>
                                ) : scanResult?.status === 'INVALID' || scanResult?.status === 'ERROR' ? (
                                    <div className="card border-2 border-secondary-subtle bg-white text-center p-4 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center">
                                        <i className="bi bi-x-circle text-danger mb-3" style={{ fontSize: '4rem' }}></i>
                                        <h4 className="fw-bold text-danger mb-2">Pase No Válido</h4>
                                        <p className="text-dark fs-6 mb-3">{scanResult.message}</p>
                                        <span className="text-muted small">Verifica que el aficionado muestre su tarjeta oficial de MVP Xolos Femenil.</span>
                                    </div>
                                ) : (
                                    /* Estado Inicial en Espera */
                                    <div className="card border-2 border-dashed border-secondary-subtle bg-white text-center p-5 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-center">
                                        <i className="bi bi-qr-code text-danger mb-3" style={{ fontSize: '4.5rem', opacity: 0.85 }}></i>
                                        <h3 className="fw-bold text-dark mb-2">Listo para Escanear</h3>
                                        <p className="text-secondary fs-6 mb-3">
                                            Apunta la pistola lectora al código QR de la tarjeta dorada del aficionado o escribe su folio.
                                        </p>
                                        <div className="d-flex align-items-center gap-2 text-danger small bg-danger-subtle px-3 py-2 rounded-pill border border-danger border-opacity-25 fw-semibold">
                                            <i className="bi bi-broadcast"></i>
                                            <span>Esperando lectura óptica o manual...</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MvpXolosZoneCheckinModal;
